<script lang="ts">
	import {
		Button,
		Col,
		Container,
		Modal,
		Row,
		Text,
		Alert,
		IconButton,
		Stack,
		Wizard,
		WizardItem,
		Loader,
		Input,
		Collapse,
		BulletedList,
	} from '@Shelly/shelly-design-system/index';

	import SettingsConfigurationWizard from './SettingsConfigurationWizard.svelte';
	import SettingsPreviewModal from './SettingsPreviewModal.svelte';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { profile_data } from 'src/stores/account/user';
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';
	import { resetEditUpdateStore } from 'src/stores/ui/edit_lists';
	import { format } from 'svelte-i18n';
	import { createEventDispatcher } from 'svelte';
	import type { Device, DeviceTemplate, Selection } from 'src/types/Device';
	import { delete_profile, save_profile } from 'src/helpers/profile_helpers';
	import {
		setWizardSteps,
		updateWizardStepOnPrevNext,
		updateWizardStepOnWizardItem,
	} from 'src/components/Lists/list-helpers';
	import {
		clearIncompatibleSelection,
		clonableSettings,
		getCompatibleDevices,
		getDeviceSwitchSettings,
		getDevicesThatCanApplySettings,
		resetSelection,
	} from 'src/helpers/settings/settings_config_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import {
		startEditingTemplate,
		cancelEditingTemplate,
		saveTemplateEdit,
		formatTemplateValue,
	} from 'src/helpers/settings/template_helpers';
	import { filterDevicesByRoom } from 'src/helpers/settings/device_filter_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	const dispatch = createEventDispatcher();

	const WIZARD_STEPS = {
		INTRODUCTION: 0,
		DEVICE_SELECTION: 1,
		TEMPLATE_SELECTION: 2,
	} as const;

	export let showModal = false;
	export let device: Device | Device[];
	export let mode: 'select' | 'manage' | 'bulk';

	$: isBulkMode = mode === 'bulk';
	$: selectedDevices = isBulkMode ? (Array.isArray(device) ? device : [device]) : [device];
	$: primaryDevice = (isBulkMode ? selectedDevices[0] : device) as Device;
	$: isMultipleDevices = isBulkMode && selectedDevices.length > 1;

	let selected: Selection = { id: '', type: '' };
	let isLoading = false;
	let error: string | null = null;
	let allDevicesRefs: { [key: string | number]: HTMLElement } = {};

	$: sourceSettings = getDeviceSwitchSettings(primaryDevice);

	let editingTemplateId: string | null = null;
	let editingTemplateName: string = '';
	let editingTemplateNotes: string = '';
	let expandedTemplates: { [key: string]: boolean } = {};
	let showPreviewModal = false;
	let previewTargetDevice: Device | null = null;
	let previewSettings: any = null;
	let previewInputConfig: any = null;
	let selectedSettingsKeys: string[] = [];

	$: compatibleDevices = (() => {
		if (!showModal) {
			return [];
		}

		if (isBulkMode) {
			const allDevices = Object.values($devices);
			const compatibleDevices = getDevicesThatCanApplySettings(allDevices);

			return compatibleDevices.filter(
				(device) =>
					!(selectedDevices as Device[]).some(
						(selectedDevice) => selectedDevice.id === device.id,
					),
			);
		} else if (isSelectMode) {
			if (sourceSettings) {
				return getCompatibleDevices(primaryDevice, sourceSettings);
			} else {
				return [];
			}
		} else {
			return [];
		}
	})();

	$: isManageMode = mode === 'manage';
	$: isSelectMode = mode === 'select';
	$: existingTemplates = Object.values($profile_data.fav_configs ?? {}) as DeviceTemplate[];

	$: compatibleDevicesList = filterDevicesByRoom(compatibleDevices);

	let wizardItems = setWizardSteps([
		$format('text_template_wizard_step_introduction', {
			default: 'Introduction',
		}),
		$format('text_template_wizard_step_select_device', {
			default: 'Select Device',
		}),
		$format('text_template_wizard_step_select_template', {
			default: 'Select Template',
		}),
	]);
	let activeStep = wizardItems[0];

	/**
	 * Determines if the user can proceed to the next wizard step.
	 * User can only choose one option from either device or template.
	 * - Step 0 (Introduction): Only if at least one setting is selected
	 * - Step 1 (Select Device): Always allowed (button shows "Apply" if device selected)
	 * - Step 2 (Select Template): Only if a template is selected
	 */
	$: canProceedToNext =
		(activeStep.activeIndex === WIZARD_STEPS.INTRODUCTION && selectedSettingsKeys.length > 0) ||
		activeStep.activeIndex === WIZARD_STEPS.DEVICE_SELECTION ||
		(activeStep.activeIndex === WIZARD_STEPS.TEMPLATE_SELECTION &&
			selected.id &&
			selected.type === 'template');

	$: isLastStepWizard = activeStep.activeIndex === wizardItems.length - 1;
	$: isFirstStep = activeStep.activeIndex === WIZARD_STEPS.INTRODUCTION;
	$: nextButton = isLastStepWizard
		? $format('text_button_apply_settings', { default: 'Apply' })
		: $format('text_button_next', { default: 'Next' });
	$: previousButton = $format('text_button_back', { default: 'Back' });

	$: deviceStepButtonText =
		activeStep.activeIndex === WIZARD_STEPS.DEVICE_SELECTION &&
		selected.id &&
		selected.type === 'device'
			? $format('text_button_apply_settings', { default: 'Apply' })
			: $format('text_button_next', { default: 'Next' });

	const initializeModal = () => {
		resetSelection(selected);
		error = null;
		editingTemplateId = null;
		editingTemplateName = '';
		editingTemplateNotes = '';
	};

	$: if (showModal) initializeModal();

	/**
	 * Copy input settings (like type) from source device to the target device.
	 */
	const applyInputSettings = async (
		sourceDeviceId: string,
		sourceChannel: number,
		targetDevice: Device,
	): Promise<boolean> => {
		const sourceDevice = readDeviceSettings(real_id(sourceDeviceId)) || {};
		const sourceInput = sourceDevice[`input:${sourceChannel}`];
		if (!sourceInput) return true;

		const targetChannel = targetDevice.channel ?? 0;

		const res = await gen2Request(targetDevice, 'Input.SetConfig', {
			id: targetDevice.channel ?? 0,
			config: { type: sourceInput.type },
		});

		if (!res.isok) {
			console.error('Failed to apply input settings', res);
			return false;
		}

		const updated = {
			...(readDeviceSettings(real_id(targetDevice.id)) || {}),
		};

		updated[`input:${targetChannel}`] = {
			...(updated[`input:${targetChannel}`] || {}),
			type: sourceInput.type,
		};

		devices_settings.update_devices_settings(real_id(targetDevice.id), updated);

		return true;
	};

	/**
	 * Apply input settings from a template to the target device.
	 */
	const applyInputSettingsFromTemplate = async (
		inputConfig: Record<string, any>,
		targetDevice: Device,
	): Promise<boolean> => {
		if (!inputConfig || !inputConfig.type) return true;

		const targetChannel = targetDevice.channel ?? 0;

		const res = await gen2Request(targetDevice, 'Input.SetConfig', {
			id: targetDevice.channel ?? 0,
			config: { type: inputConfig.type },
		});

		if (!res.isok) {
			console.error('Failed to apply input settings from template', res);
			return false;
		}

		const updated = {
			...(devices_settings.getByRealId(targetDevice.id) || {}),
		};

		updated[`input:${targetChannel}`] = {
			...(updated[`input:${targetChannel}`] || {}),
			type: inputConfig.type,
		};

		devices_settings.update_devices_settings(real_id(targetDevice.id), updated);

		return true;
	};

	/**
	 * Applies configuration settings to a target device
	 *
	 * @param input - Object containing either a config object or device information
	 * @param input.config - Direct configuration object (for templates)
	 * @param input.id & input.channel - Device ID & channel (for device-to-device copying)
	 * @param targetDevice - The device to apply settings to
	 *
	 * @returns Promise that resolves when the settings are applied
	 */
	const applySettings = async (input: any, targetDevice: Device): Promise<boolean> => {
		try {
			if (input.id) {
				const ok = await applyInputSettings(input.id, input.channel || 0, targetDevice);
				if (!ok) return false;
			} else if (input.inputConfig) {
				const ok = await applyInputSettingsFromTemplate(input.inputConfig, targetDevice);
				if (!ok) return false;
			}

			const allSettingsToApply =
				input?.config ?? readDeviceSettings(real_id(input.id))?.[`switch:${input.channel}`];

			if (!allSettingsToApply) {
				console.error('Invalid target or template config');
				return false;
			}

			const filteredSettings = {};
			selectedSettingsKeys.forEach((key) => {
				if (allSettingsToApply[key] !== undefined) {
					filteredSettings[key] = allSettingsToApply[key];
				}
			});

			if (Object.keys(filteredSettings).length === 0) {
				console.log('No settings selected to apply');
				return true;
			}

			const res = await gen2Request(targetDevice, 'switch.setconfig', {
				config: filteredSettings,
				id: targetDevice.channel || 0,
			});

			const response = onSettingsResponse(res, targetDevice);
			if (response?.isok) {
				const updated = {
					...(readDeviceSettings(real_id(targetDevice.id)) || {}),
				};

				updated[`switch:${targetDevice.channel}`] = {
					...(updated[`switch:${targetDevice.channel}`] || {}),
					...filteredSettings,
				};

				devices_settings.update_devices_settings(real_id(targetDevice.id), updated);
				return true;
			} else {
				console.error('Settings application failed:', response.error);
				return false;
			}
		} catch (err) {
			console.error('Error applying settings:', err);
			return false;
		}
	};

	const handleApply = () => {
		if (selected.type === 'template') {
			const template = existingTemplates.find((t) => t.id === selected.id);
			if (template) {
				previewTargetDevice = primaryDevice;
				previewSettings = template.config;
				previewInputConfig = template.inputConfig;
				showPreviewModal = true;
			}
		} else if (selected.type === 'device') {
			const target = compatibleDevices.find((d) => d.id === selected.id);
			if (target) {
				previewTargetDevice = primaryDevice;

				previewSettings = readDeviceSettings(real_id(target.id))?.[`switch:${target.channel}`];
				previewInputConfig = readDeviceSettings(real_id(target.id))?.[`input:${target.channel}`];
				showPreviewModal = true;
			}
		}
	};

	const handleClose = () => {
		showModal = false;
		resetSelection(selected);
		error = null;
		const { id, name, notes } = cancelEditingTemplate();
		editingTemplateId = id;
		editingTemplateName = name;
		editingTemplateNotes = notes;

		compatibleDevices = [];
		sourceSettings = undefined;
		dispatch('close');
	};

	const handleDeleteTemplate = async (templateId: string) => {
		try {
			const currentTemplates = $profile_data.fav_configs ?? {};
			if (!(templateId in currentTemplates)) return;

			const { [templateId]: _, ...updatedTemplates } = $profile_data.fav_configs ?? {};

			if (Object.keys(updatedTemplates).length === 0) {
				await delete_profile('fav_configs');
			} else {
				await save_profile('fav_configs', updatedTemplates);
			}

			if (selected.id === templateId) {
				resetSelection(selected);
			}
		} catch (err) {
			console.error('Error deleting template:', err);
			error = $format('text_template_error_delete_template', {
				default: 'Failed to delete template. Please try again.',
			});
		}
	};

	const onClickWizardItem = (e: MouseEvent, index: number) => {
		const result = updateWizardStepOnWizardItem(index, wizardItems, activeStep);
		wizardItems = result.wizardItems;
		activeStep = result.activeWizardStep;

		clearIncompatibleSelection(activeStep.activeIndex, selected);
	};

	const onClickPrevNextButton = (type: 'prev' | 'next') => {
		const result = updateWizardStepOnPrevNext(type, wizardItems, activeStep);
		wizardItems = result.wizardItems;
		activeStep = result.activeWizardStep;

		clearIncompatibleSelection(activeStep.activeIndex, selected);
	};

	const handleNextOrApply = () => {
		const shouldApplyImmediately =
			isLastStepWizard ||
			(activeStep.activeIndex === WIZARD_STEPS.DEVICE_SELECTION &&
				selected.id &&
				selected.type === 'device');

		if (shouldApplyImmediately) {
			handleApply();
		} else {
			onClickPrevNextButton('next');
		}
	};

	const handlePreviewConfirm = async () => {
		showPreviewModal = false;
		isLoading = true;
		error = null;

		let success = false;
		if (selected.type === 'template') {
			const template = existingTemplates.find((t) => t.id === selected.id);
			if (template) {
				if (isBulkMode) {
					const results = await Promise.all(
						(selectedDevices as Device[]).map((targetDevice) =>
							applySettings(
								{
									config: template.config,
									inputConfig: template.inputConfig,
								},
								targetDevice,
							),
						),
					);
					success = results.every((result) => result);
				} else {
					success = await applySettings(
						{
							config: template.config,
							inputConfig: template.inputConfig,
						},
						primaryDevice,
					);
				}
			}
		} else if (selected.type === 'device') {
			const target = compatibleDevices.find((d) => d.id === selected.id);
			if (target) {
				if (isBulkMode) {
					const results = await Promise.all(
						(selectedDevices as Device[]).map((targetDevice) =>
							applySettings(
								{
									id: target.id,
									channel: target.channel || 0,
								},
								targetDevice,
							),
						),
					);
					success = results.every((result) => result);
				} else {
					success = await applySettings(
						{
							id: target.id,
							channel: target.channel || 0,
						},
						primaryDevice,
					);
				}
			}
		}

		isLoading = false;

		if (!success) {
			error = $format('text_template_error_apply_settings', {
				default: 'Failed to apply settings. Please try again.',
			});
		} else {
			if (isBulkMode) {
				resetEditUpdateStore();
			}
			handleClose();
		}
	};

	const handlePreviewCancel = () => {
		showPreviewModal = false;
		previewTargetDevice = null;
		previewSettings = null;
		previewInputConfig = null;
	};
</script>

<Modal
	on:close={handleClose}
	{showModal}
	title={$format(
		isSelectMode ? 'text_template_modal_title_apply' : 'text_template_modal_title_manage',
		{
			default: isSelectMode ? 'Apply settings configuration' : 'Manage templates',
		},
	)}
>
	<Wizard slot="toolbar">
		{#if isSelectMode}
			{#each wizardItems as item, i (i)}
				<WizardItem
					on:click={(e) => onClickWizardItem(e, i)}
					active={item.active}
					title={item.title}
					disabled={false}
					showMoreDots={item.showMoreDots}
					indicatorColor={$showNewLayout ? 'color-2' : undefined}
				/>
			{/each}
		{/if}
	</Wizard>

	<Container slot="content">
		{#if error}
			<Alert
				type="danger"
				heading={$format('text_template_error_heading', { default: 'Error' })}
				message={error}
				class="mb-m"
			/>
		{/if}

		{#if isLoading}
			<div class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
				<Stack gap="m" class="text-center">
					<Loader size="l" />
					<Text styleType="body">
						{$format('text_template_loading_applying_settings', {
							default: 'Applying settings...',
						})}
					</Text>
				</Stack>
			</div>
		{:else if isManageMode}
			<Stack gap="m">
				{#if existingTemplates.length > 0}
					{#each existingTemplates as template}
						<Row class="mb-s">
							<Col sm="8" md="9">
								{#if editingTemplateId === template.id}
									<!-- Edit mode -->
									<form autocomplete="off" novalidate>
										<Stack gap="s">
											<Input
												id="template-name-input"
												icon={null}
												type="text"
												label={$format('text_template_edit_name_label', {
													default: 'Template Name',
												})}
												aria-label={$format('text_template_edit_name_label', {
													default: 'Template Name',
												})}
												bind:value={editingTemplateName}
												placeholder={$format(
													'text_template_edit_name_placeholder',
													{
														default: 'Enter template name',
													},
												)}
											/>
											<Input
												id="template-notes-input"
												icon={null}
												type="text"
												label={$format('text_template_edit_notes_label', {
													default: 'Notes (optional)',
												})}
												aria-label={$format('text_template_edit_notes_label', {
													default: 'Notes (optional)',
												})}
												bind:value={editingTemplateNotes}
												placeholder={$format(
													'text_template_edit_notes_placeholder',
													{
														default: 'Enter notes',
													},
												)}
											/>
											<div class="d-flex gap-s">
												<Button
													size="s"
													on:click={async () => {
														const { newError, id, name, notes } =
															await saveTemplateEdit(
																editingTemplateId,
																editingTemplateName,
																editingTemplateNotes,
																error,
															);
														error = newError;
														editingTemplateId = id;
														editingTemplateName = name;
														editingTemplateNotes = notes;
													}}
													disabled={!editingTemplateName.trim()}
												>
													{$format('button_save', { default: 'Save' })}
												</Button>
												<Button
													variant="secondary"
													size="s"
													on:click={() => {
														const { id, name, notes } =
															cancelEditingTemplate();
														editingTemplateId = id;
														editingTemplateName = name;
														editingTemplateNotes = notes;
													}}
												>
													>
													{$format('button_cancel', { default: 'Cancel' })}
												</Button>
											</div>
										</Stack>
									</form>
								{:else}
									<!-- View mode -->
									<Collapse
										id={template.id}
										title={template.name}
										expanded={expandedTemplates[template.id] || false}
										on:click={() => {
											expandedTemplates[template.id] =
												!expandedTemplates[template.id];
											expandedTemplates = { ...expandedTemplates };
										}}
									>
										{#each clonableSettings() as setting}
											{#if setting.key in template.config}
												<BulletedList
													titleStyleType="body-small"
													descriptionStyleType="body-small"
													list={[
														{
															title: setting.title,
															description: formatTemplateValue(
																setting.key,
																template.config[setting.key],
															),
														},
													]}
												></BulletedList>
											{/if}
										{/each}

										{#if template.notes}
											<Text
												styleType="body-extra-small"
												class="text-color-muted mt-m"
											>
												{$format('text_template_notes', {
													default: 'Notes:',
												})}
												{template.notes}
											</Text>
										{/if}
									</Collapse>
								{/if}
							</Col>
							<Col sm="4" md="3">
								<div class="d-flex gap-s justify-content-end">
									{#if editingTemplateId !== template.id}
										<IconButton
											aria-label={$format('button_edit_template', {
												default: 'Edit template',
											})}
											icon="fas fa-edit"
											variant="secondary"
											size="s"
											on:click={() => {
												const { id, name, notes } =
													startEditingTemplate(template);
												editingTemplateId = id;
												editingTemplateName = name;
												editingTemplateNotes = notes;
											}}
											tooltip={$format('text_template_edit_tooltip', {
												default: 'Edit template',
											})}
										/>
									{/if}
									<IconButton
										aria-label={$format('button_delete_template', {
											default: 'Delete template',
										})}
										icon="fas fa-trash"
										variant="danger"
										size="s"
										on:click={() => handleDeleteTemplate(template.id)}
										tooltip={$format('text_template_delete_tooltip', {
											default: 'Delete template',
										})}
									/>
								</div>
							</Col>
						</Row>
					{/each}
				{:else}
					<Alert
						type="info"
						heading={$format('text_no_templates_heading', {
							default: 'No Templates Available',
						})}
						message={$format('text_no_templates_message', {
							default: 'No saved templates found.',
						})}
						class="mb-s"
					/>
				{/if}
			</Stack>
		{:else}
			<!-- Full wizard content for select mode -->
			<SettingsConfigurationWizard
				{activeStep}
				{wizardItems}
				bind:selected
				bind:selectedSettingsKeys
				{compatibleDevices}
				{compatibleDevicesList}
				{allDevicesRefs}
				{isBulkMode}
				{isMultipleDevices}
				{selectedDevices}
				{existingTemplates}
			/>
		{/if}
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		{#if isManageMode}
			<Button variant="secondary" class="min-width-md-m text-nowrap" on:click={handleClose}>
				{$format('button_close', { default: 'Close' })}
			</Button>
		{:else}
			{#if !isFirstStep}
				<Button
					variant="secondary"
					class="min-width-md-m text-nowrap mr-s"
					on:click={() => onClickPrevNextButton('prev')}
				>
					{previousButton}
				</Button>
			{/if}
			<Button
				aria-labelledby="button-nextbutton-apply-or-next"
				id="button-nextbutton-apply-or-next"
				class="min-width-md-m text-nowrap"
				disabled={!canProceedToNext || isLoading}
				on:click={handleNextOrApply}
			>
				{#if isLoading}
					<Loader size="m" />
				{:else if activeStep.activeIndex === 1}
					{deviceStepButtonText}
				{:else}
					{nextButton}
				{/if}
			</Button>
		{/if}
	</div>
</Modal>

{#if showPreviewModal && previewTargetDevice}
	<SettingsPreviewModal
		bind:showModal={showPreviewModal}
		device={previewTargetDevice}
		settingsToApply={previewSettings}
		{selectedSettingsKeys}
		on:confirm={handlePreviewConfirm}
		on:cancel={handlePreviewCancel}
	/>
{/if}
